import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { SliceKeys, tablePaginationInitialValue } from "@sso/constant";
import { RootState } from "@sso/store";
import { SearchColumn } from "@sso/types";
import { getTableSortColumn, queryBuilder } from "@sso/utils";

export const SoftwareLicenceSlice = createSlice({
  name: SliceKeys.SOFTWARE_LICENCE,
  initialState: { softwareId: "", pagination: tablePaginationInitialValue },
  reducers: {
    setSoftwareLicenceSoftwareId: (state, action: PayloadAction<string>) => {
      state.softwareId = action.payload;
    },
    setSoftwareLicencePageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageIndex = tablePaginationInitialValue.pageIndex;
      state.pagination.pageSize = action.payload;
    },
    setSoftwareLicencePageIndex: (state, action: PayloadAction<number>) => {
      state.pagination.pageIndex = action.payload;
    },
    setSoftwareLicenceSearchColumn: (state, action: PayloadAction<SearchColumn>) => {
      state.pagination.pageIndex = tablePaginationInitialValue.pageIndex;
      state.pagination.search[action.payload.column] = action.payload;
    },
    clearSoftwareLicenceSearchColumn: (state, action: PayloadAction<string>) => {
      const { [action.payload]: deleteItem, ...otherSearchItems } = state.pagination.search;
      state.pagination.search = otherSearchItems;
    },
    setSoftwareLicenceSortColumn: (state, action: PayloadAction<string>) => {
      const { column, type } = state.pagination.sort;
      state.pagination.sort = getTableSortColumn(column, action.payload, type);
      state.pagination.pageIndex = tablePaginationInitialValue.pageIndex;
    },
  },
});

export const {
  setSoftwareLicenceSoftwareId,
  setSoftwareLicencePageSize,
  setSoftwareLicencePageIndex,
  setSoftwareLicenceSortColumn,
  setSoftwareLicenceSearchColumn,
  clearSoftwareLicenceSearchColumn,
} = SoftwareLicenceSlice.actions;

export const selectSoftwareLicenceId = (state: RootState) => state.softwareLicence.softwareId;

export const selectSoftwareLicencePagination = createSelector(
  (state: RootState) => state.softwareLicence.pagination,
  ({ pageSize, totalPages, pageIndex, sort, search }) => ({
    pageSize: { action: setSoftwareLicencePageSize, value: pageSize },
    pagination: {
      totalPages,
      pageIndex,
      action: setSoftwareLicencePageIndex,
    },
    sort,
    search: { object: search, onChange: setSoftwareLicenceSearchColumn, onClear: clearSoftwareLicenceSearchColumn },
  }),
);

// needs to build query with software id too
export const selectSoftwareLicenceQuery = createSelector(
  (state: RootState) => state.softwareLicence,
  ({ pagination, softwareId }) =>
    `${queryBuilder(
      pagination.pageSize,
      pagination.pageIndex,
      pagination.search,
      pagination.sort,
    )}&softwareId=${softwareId}`,
);

export default SoftwareLicenceSlice.reducer;
